import { configureApiClient } from '@echo/api-client/client';
import {
  allowsPseudoLocale,
  clientHeaders,
  FakeAuthProvider,
  lazyGroupChatTransport,
  OnEarlyAuthProvider,
  SessionManager,
} from '@echo/core';
import {
  configureExerciseMinutes,
  configureExerciseProgress,
  configureLargeText,
  EchoProvider,
  markUpdateRequired,
  useLargeText,
} from '@echo/core/react';
import { type AppLocale, i18n } from '@echo/i18n';
import { LocaleProvider } from '@echo/i18n/react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { appConfig, groupChatEnabled } from './lib/config';
import { applyDocumentLocale, detectLocale, storeLocale } from './lib/locale';
import {
  BrowserTokenStorage,
  localStorageExerciseMinutes,
  localStorageExerciseProgress,
  localStorageLargeText,
  withBrowserRefreshLock,
} from './lib/storage';

/** Composition root — the only file that knows this is a browser. */
// Real OnEarly auth by default; the fake provider is opt-in for local work without a Core
// checkout (VITE_USE_REAL_AUTH=0) and never leaves `local`.
const useFakeAuth = appConfig.environment === 'local' && import.meta.env.VITE_USE_REAL_AUTH === '0';

export const sessionManager = new SessionManager({
  provider: useFakeAuth
    ? new FakeAuthProvider()
    : new OnEarlyAuthProvider({ platform: 'web', ...appConfig.auth }),
  storage: new BrowserTokenStorage(),
  withLock: withBrowserRefreshLock,
});

configureApiClient({
  onearlyBaseUrl: appConfig.onearlyApiUrl,
  echoBaseUrl: appConfig.echoApiUrl,
  getAccessToken: () => sessionManager.getAccessToken(),
  // After a 421 WRONG_REGION: the token's home-region claim was missing or stale.
  refreshAccessToken: () => sessionManager.refreshNow(),
  getLocale: () => i18n.locale,
  headers: clientHeaders('web', appConfig.appVersion),
  // Core refused this build as too old: the update screen covers the app (`UpdateGate`).
  onUnsupportedAppVersion: markUpdateRequired,
});

// Group chat on Reach, off unless the build set VITE_GROUP_CHAT=1 (docs/group-chat.md). Reach's
// client is a separate chunk, fetched the first time a screen uses chat.
const groupChat = groupChatEnabled
  ? lazyGroupChatTransport(async () =>
      (await import('@echo/core/reach')).createReachGroupChatTransport({
        reach: appConfig.reach,
        getAccessToken: () => sessionManager.getAccessToken(),
      }),
    )
  : undefined;
sessionManager.subscribe((session) => {
  if (session.status === 'anonymous' || session.status === 'expired') groupChat?.reset();
});

// Quick math left after an answer, the exercise duration and large text live in the browser,
// like the phone keeps them on the device: Core only records finished attempts.
configureExerciseProgress(localStorageExerciseProgress);
configureExerciseMinutes(localStorageExerciseMinutes);
configureLargeText(localStorageLargeText);

/** The pseudo-locale is a troubleshooting tool for local builds, never a customer language. */
const allowPseudo = allowsPseudoLocale(appConfig.environment);

export function AppProviders({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<AppLocale>(() => detectLocale(allowPseudo));
  useEffect(() => applyDocumentLocale(locale), [locale]);
  // Large text: every type level at 150% (globals.css › [data-large-text]).
  const largeText = useLargeText();
  useEffect(() => {
    document.documentElement.toggleAttribute('data-large-text', largeText);
  }, [largeText]);
  const changeLocale = useCallback((next: AppLocale) => {
    storeLocale(next);
    setLocale(next);
  }, []);

  // Like `app`: a tab becoming visible or the network returning refreshes an about-to-expire
  // token early. Failures surface through the session, not here.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        void sessionManager.getAccessToken().catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('online', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('online', refresh);
    };
  }, []);

  return (
    <LocaleProvider locale={locale} onLocaleChange={changeLocale} allowPseudo={allowPseudo}>
      <EchoProvider
        config={appConfig}
        sessionManager={sessionManager}
        groupChatTransport={groupChat}
      >
        {children}
      </EchoProvider>
    </LocaleProvider>
  );
}
